import React, { useEffect, useState, useRef, useCallback } from "react";
import { Event } from "../types/index";
import EventInfobar from "./event/infobar";
import { useAppDispatch, useAppSelector } from "../../../hooks/store-hooks";
import { setSelectedEvent, setStep } from "../../../store/slices/eventSlice";
import EventCard from "./event/card";
import {
  useLazyGetEventByIdQuery,
  useGetMyEventsQuery,
  useGetEventsQuery,
} from "../../../store/api/eventApi";
import { RootState } from "../../../store/store";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import { useLocation } from "react-router-dom";

const EventFeed: React.FC<{ className?: string }> = ({ className }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { selectedEvent, event_type, city, sort_by } = useAppSelector(
    (state) => state.event,
  );
  const { is_active } = useSelector((state: RootState) => state.event);

  const [myEvent, setMyEvent] = useState(false);
  const [offset, setOffset] = useState(0);
  const [isEnd, setIsEnd] = useState(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [onDispatch, setOnDispatch] = useState(false);

  const [trigger, { data: event }] = useLazyGetEventByIdQuery();

  const observer = useRef<IntersectionObserver | null>(null);

  const { data: myEvents, refetch: myEventsRefetch } = useGetMyEventsQuery();
  const {
    data: eventsData,
    isLoading: loadingEvents,
    isFetching,
    refetch: eventsRefetch,
  } = useGetEventsQuery({
    is_active,
    event_type,
    city,
    sort_by,
    limit: is_active ? 25 : 10,
    offset: is_active ? 0 : offset,
  });

  useEffect(() => {
    if (eventsData?.data?.is_end) {
      setIsEnd(true);
    }
    if (eventsData?.data?.events && !is_active) {
      setEvents((prevEvents) => [...prevEvents, ...eventsData.data.events]);
    } else {
      setEvents(eventsData?.data?.events);
    }
  }, [eventsData]);

  const lastEventElementRef = useCallback(
    (node: HTMLDivElement) => {
      if (loadingEvents || isFetching || isEnd || is_active) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !isEnd) {
          setOffset((prevOffset) => prevOffset + 10);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loadingEvents, isFetching, isEnd, is_active],
  );

  useEffect(() => {
    setOffset(0);
    setIsEnd(false);
    setEvents([]); // Reset events when filters change
  }, [is_active, event_type, city, sort_by]);

  const handleEventClick = (
    event: Event,
    isMyEvent: boolean,
    isMemberOnly?: boolean,
  ) => {
    if (isMemberOnly) {
      navigate(ROUTES.HOME);
    } else {
      dispatch(setSelectedEvent(event));
      dispatch(setStep(2));
      setMyEvent(isMyEvent);
    }
  };

  const handleCloseOverlay = () => {
    navigate(ROUTES.EVENTS);
    dispatch(setStep(1));
    dispatch(setSelectedEvent(null));
  };

  useEffect(() => {
    if (
      location.pathname.startsWith("/events/") &&
      location.pathname.split("/").length === 1
    ) {
      const eventId = location.pathname.split("/")[2];
      trigger({ id: eventId });

      const isMyEvent = myEvents?.data?.events?.some(
        (event: Event) => event.id === eventId,
      );
      if (isMyEvent) {
        setMyEvent(true);
      }
      dispatch(setSelectedEvent(event));
      dispatch(setStep(2));
      return;
    }
  }, [location.pathname, myEvents, event]);

  useEffect(() => {
    document.body.style.overflow = selectedEvent ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [selectedEvent]);

  useEffect(() => {
    if (onDispatch) {
      dispatch(setStep(1));
      dispatch(setSelectedEvent(null));
      myEventsRefetch();
      eventsRefetch();
      setOnDispatch(false);
    }
  }, [onDispatch]);

  window.addEventListener("message", (event) => {
    if (event.origin === process.env.REACT_APP_PAYMENT_BASE_URL) {
      if (event.data.refetchData) {
        setOnDispatch(true);
      }
    }
  });

  const fetchEvents = () => {
    myEventsRefetch();
    eventsRefetch();
  };

  return (
    <div className={`relative ${className}`}>
      {is_active && (
        <>
          <div
            className={`text-white mb-4 text-[18px] ${
              myEvents?.data?.events?.length === 0 && "hidden"
            }`}
          >
            My Events
          </div>
          <div className="flex space-x-4 overflow-x-auto scrollbar-hide">
            {myEvents?.data?.events?.map((event: Event) => (
              <div
                className="flex-shrink-0 w-full md:w-1/3 2xl:w-1/4"
                key={event.id}
              >
                <EventCard
                  event={event}
                  onClick={handleEventClick}
                  isMyEvent={true}
                />
              </div>
            ))}
          </div>
        </>
      )}
      {events.length ? (
        <>
          <div
            className={`text-white my-6 text-[18px] ${!is_active && "hidden"}`}
          >
            Upcoming Events
          </div>
          <div className="flex flex-wrap gap-6 w-full">
            {events.map((event: Event, index) => (
              <EventCard
                event={event}
                key={event.id}
                onClick={handleEventClick}
                isMyEvent={false}
                ref={index === events.length - 1 ? lastEventElementRef : null}
              />
            ))}
          </div>
        </>
      ) : (
        <div className="flex flex-col justify-center items-center text-white md:mt-20 ">
          <img
            src="/assets/noevents.png"
            alt="No events"
            className="h-40 w-40 mb-4"
          />
          <p className="text-xl font-semibold bg-gradient-text text-transparent bg-clip-text mb-6">
            NO EVENTS FOUND
          </p>
          <p className="text-center md:w-2/5">
            We couldn't find any events that match your filter criteria. Please
            try adjusting your filters or check back later for new events.
          </p>
        </div>
      )}
      {selectedEvent && (
        <>
          <div
            className="fixed inset-0 bg-black opacity-50 z-40"
            onClick={handleCloseOverlay}
          ></div>
          <EventInfobar
            event={selectedEvent}
            isOpen
            onClose={handleCloseOverlay}
            isMyEvent={myEvent}
            fetchEvents={fetchEvents}
          />
        </>
      )}
    </div>
  );
};

export default EventFeed;
